import {Injectable} from '@angular/core';

/**
 * Key/value persistence for small UI preferences (release notes, nudges, last-used options).
 *
 * Backed by localStorage, falling back to an in-memory map when storage is unavailable or
 * throws (e.g. some embedded webviews, blocked site data). Callers should treat values as
 * best-effort: they may not survive a reload in those environments.
 */
@Injectable({providedIn: 'root'})
export class StorageService {
  private readonly memory = new Map<string, string>();

  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return this.memory.get(key) ?? null;
    }
  }

  set(key: string, value: string): void {
    this.memory.set(key, value);

    try {
      localStorage.setItem(key, value);
    } catch {
      // Keep the in-memory copy so the value holds for the rest of the session
    }
  }

  remove(key: string): void {
    this.memory.delete(key);

    try {
      localStorage.removeItem(key);
    } catch {
      // Nothing to clean up beyond the in-memory copy
    }
  }
}
